export default function useProgramAgeGroupSelection({ draft, schema, ageGroups, translate }) {
  const requireProgramAgeGroupSelection = computed(() => {
    return schema.value?.meta?.validations?.require_program_age_group_selection === true;
  });

  const selectedProgramAgeGroupId = computed({
    get() {
      const ids = draft.value?.age_group_ids;
      if (!Array.isArray(ids) || ids.length === 0) {
        return null;
      }
      return ids[0] ?? null;
    },
    set(newId) {
      setSelectedProgramAgeGroupId(newId);
    },
  });

  const selectedProgramAgeGroup = computed(() => {
    const id = selectedProgramAgeGroupId.value;
    if (!id) {
      return null;
    }
    return ageGroups.value?.find((ageGroup) => ageGroup.id === id) || null;
  });

  const saveDisabledWithoutAgeGroupSelection = computed(() => {
    if (!requireProgramAgeGroupSelection.value) {
      return false;
    }
    return !selectedProgramAgeGroupId.value;
  });

  const saveDisabledWithoutAgeGroupSelectionTooltip = computed(() => {
    if (!saveDisabledWithoutAgeGroupSelection.value) {
      return null;
    }
    return translate('Select an age group to save this program.');
  });

  const ageMinMaxValidationError = computed(() => {
    if (!draft.value) {
      return null;
    }
    const min = toNullableMonthNumber(draft.value.age_min);
    const max = toNullableMonthNumber(draft.value.age_max);
    if (min == null || max == null || min <= max) {
      return null;
    }
    return translate('Youngest age accepted must be less than or equal to oldest age accepted.');
  });

  const ageGroupBoundsValidationError = computed(() => {
    if (!requireProgramAgeGroupSelection.value || !selectedProgramAgeGroup.value || !draft.value) {
      return null;
    }
    const min = toNullableMonthNumber(draft.value.age_min);
    const max = toNullableMonthNumber(draft.value.age_max);
    const lowerBound = selectedProgramAgeGroup.value.min;
    const upperBound = selectedProgramAgeGroup.value.max;
    const minOutOfBounds = min != null && (min < lowerBound || min > upperBound);
    const maxOutOfBounds = max != null && (max < lowerBound || max > upperBound);
    if (!minOutOfBounds && !maxOutOfBounds) {
      return null;
    }
    return translate('Ages must stay within the selected age group.');
  });

  const saveDisabledDueToProgramAgeValidation = computed(() => {
    return (
      saveDisabledWithoutAgeGroupSelection.value ||
      !!ageMinMaxValidationError.value ||
      !!ageGroupBoundsValidationError.value
    );
  });

  const saveDisabledDueToProgramAgeValidationTooltip = computed(() => {
    return (
      saveDisabledWithoutAgeGroupSelectionTooltip.value ||
      ageMinMaxValidationError.value ||
      ageGroupBoundsValidationError.value
    );
  });

  function ageMinAtOrBelowAgeMaxRule() {
    return ageMinMaxValidationError.value || true;
  }

  const programAgeRangeBounds = computed(() => {
    if (!requireProgramAgeGroupSelection.value || !selectedProgramAgeGroup.value) {
      return { min: undefined, max: undefined };
    }
    return {
      min: selectedProgramAgeGroup.value.min,
      max: selectedProgramAgeGroup.value.max,
    };
  });

  function initializeProgramAgeGroupSelection() {
    applySchemaAgeMinMaxDefaults();
    if (!draft.value) {
      return;
    }
    if (requireProgramAgeGroupSelection.value) {
      normalizeAgeGroupIds();
      applySelectedProgramAgeGroup({ fillMissingAgeBoundsOnly: true });
    }
  }

  function setSelectedProgramAgeGroupId(newId) {
    if (!draft.value) {
      return;
    }
    if (newId == null || newId === '') {
      clearProgramAgeGroup();
      return;
    }
    draft.value.age_group_ids = [newId];
    if (requireProgramAgeGroupSelection.value) {
      applySelectedProgramAgeGroup({ fillMissingAgeBoundsOnly: false });
    }
  }

  function clearProgramAgeGroup() {
    draft.value.age_group_ids = [];
    if (!requireProgramAgeGroupSelection.value) {
      return;
    }
    draft.value.name = '';
    draft.value.age_min = undefined;
    draft.value.age_max = undefined;
    applySchemaAgeMinMaxDefaults();
  }

  function normalizeAgeGroupIds() {
    if (!Array.isArray(draft.value.age_group_ids) || draft.value.age_group_ids.length <= 1) {
      return;
    }
    draft.value.age_group_ids = [draft.value.age_group_ids[0]];
  }

  function applySelectedProgramAgeGroup({ fillMissingAgeBoundsOnly }) {
    const ageGroup = selectedProgramAgeGroup.value;
    if (!ageGroup || !draft.value) {
      return;
    }
    draft.value.name = ageGroup.name;
    if (fillMissingAgeBoundsOnly) {
      if (draft.value.age_min == null) {
        draft.value.age_min = ageGroup.min;
      }
      if (draft.value.age_max == null) {
        draft.value.age_max = ageGroup.max;
      }
      return;
    }
    draft.value.age_min = ageGroup.min;
    draft.value.age_max = ageGroup.max;
  }

  function applySchemaAgeMinMaxDefaults() {
    if (!draft.value) {
      return;
    }
    if (draft.value.age_min === undefined) {
      const ageMinSchema = schema.value?.definition?.properties?.age_min;
      draft.value.age_min = ageMinSchema?.minimum || null;
    }
    if (draft.value.age_max === undefined) {
      const ageMaxSchema = schema.value?.definition?.properties?.age_max;
      draft.value.age_max = ageMaxSchema?.maximum || null;
    }
  }

  function toNullableMonthNumber(value) {
    if (value == null || value === '') {
      return null;
    }
    const numeric = Number(value);
    return Number.isNaN(numeric) ? null : numeric;
  }

  return {
    ageMinAtOrBelowAgeMaxRule,
    initializeProgramAgeGroupSelection,
    programAgeRangeBounds,
    requireProgramAgeGroupSelection,
    saveDisabledDueToProgramAgeValidation,
    saveDisabledDueToProgramAgeValidationTooltip,
    saveDisabledWithoutAgeGroupSelection,
    saveDisabledWithoutAgeGroupSelectionTooltip,
    selectedProgramAgeGroupId,
  };
}
